<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { ColoredButton, SelectInput } from '@/shared/ui';
  import {
    buildPermissionFieldGroups,
    type PermissionFieldGroup,
  } from '../../lib/permission-fields-report';
  import { resolveVisiblePermissionSubjects } from '../../lib/permission-subject-filters';
  import { useFormBuilderStore } from '../../model/form-builder.store';
  import { useFormPermissionsStore } from '../../model/form-permissions.store';
  import PermissionsFieldsWithPermissions from './PermissionsFieldsWithPermissions.vue';
  import PermissionSubjectList from './PermissionSubjectList.vue';
  import type {
    PermissionSubject,
    PermissionSubjectMode,
  } from '../../composables/usePermissionSubjects';

  const props = defineProps<{
    positions: PermissionSubject[];
    functionalActivities: PermissionSubject[];
    isLoadingPositions?: boolean;
    isLoadingFunctionalActivities?: boolean;
    hasPositionsError?: boolean;
    hasFunctionalActivitiesError?: boolean;
  }>();

  const SUBJECT_OPTIONS = [
    { id: 'positions', name: 'Cargos' },
    { id: 'functionalActivities', name: 'Atividades Funcionais' },
  ];

  const formBuilderStore = useFormBuilderStore();
  const formPermissionsStore = useFormPermissionsStore();

  const selectedMode = ref<PermissionSubjectMode>('positions');
  const selectedIds = ref<string[]>([]);
  const fieldGroups = ref<PermissionFieldGroup[] | null>(null);

  const isPositions = computed(() => selectedMode.value === 'positions');
  const items = computed(() => (isPositions.value ? props.positions : props.functionalActivities));
  const isLoading = computed(() =>
    isPositions.value ? props.isLoadingPositions : props.isLoadingFunctionalActivities
  );
  const hasError = computed(() =>
    isPositions.value ? props.hasPositionsError : props.hasFunctionalActivitiesError
  );
  const viewGroups = computed(() =>
    (fieldGroups.value ?? []).map((group) => ({ name: group.name, fields: group.fieldLabels }))
  );

  const handleModeChange = (value: string | number) => {
    selectedMode.value = value as PermissionSubjectMode;
    selectedIds.value = [];
    fieldGroups.value = null;
  };

  const handleViewPermissions = () => {
    const selectedSubjects = resolveVisiblePermissionSubjects(items.value, '', true).filter((item) =>
      selectedIds.value.includes(String(item.id))
    );

    fieldGroups.value = buildPermissionFieldGroups(
      selectedMode.value,
      selectedSubjects,
      formPermissionsStore.grantsByFieldId,
      formBuilderStore.elements
    );
  };
</script>

<template>
  <div
    class="flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-6"
    data-testid="permissions-view-tab"
  >
    <div class="flex w-full flex-col gap-2 rounded-lg border border-gray-02 p-4">
      <p class="text-sm text-gray-11">Selecione:</p>

      <SelectInput
        :model-value="selectedMode"
        :options="SUBJECT_OPTIONS"
        button-class="bg-white-01 border border-gray-02 h-11"
        shadow-class="shadow-[0_1px_2px_rgba(16,24,40,0.05)]"
        data-testid="permissions-subject-select"
        @update:model-value="handleModeChange"
      />

      <PermissionSubjectList
        v-model:selected-ids="selectedIds"
        class="mt-2"
        :mode="selectedMode"
        :items="items"
        :loading="isLoading"
        :has-error="hasError"
      />

      <ColoredButton
        class="mt-2 !h-[42px] w-full justify-center"
        text="Ver permissões"
        :disabled="selectedIds.length === 0"
        data-testid="permissions-view-btn"
        @click="handleViewPermissions"
      />
    </div>

    <PermissionsFieldsWithPermissions v-if="fieldGroups !== null" :groups="viewGroups" />
  </div>
</template>
